import { zodResolver } from "@hookform/resolvers/zod";
import { Button , Calendar , Input , Label , Popover, PopoverContent, PopoverTrigger } from "@roberttamas/ui";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { CalendarIcon, Check, ChevronLeft, ChevronRight, Loader2 } from "@/components/icons";
import { LanguageCombobox } from "@/components/language-combobox";
import { buildSiteFormSchema, type SiteFormValues } from "@/components/site-form";
import type { TranslationKey } from "@/i18n";
import { checkSlug, listVariants } from "@/lib/api";
import { currentDateFnsLocale } from "@/lib/locale-format";
import { formatLocaleLabelWithTag } from "@/lib/locales";
import { isValidSlug, slugify } from "@/lib/slug";
import { uploaderDisplayPath } from "@/lib/uploader-url";
import { cn } from "@/lib/utils";

interface Props {
  submitting?: boolean;
  onSubmit: (values: SiteFormValues) => Promise<void> | void;
  serverFieldErrors?: Partial<Record<keyof SiteFormValues, string>>;
}

const STEP_KEYS: TranslationKey[] = [
  "siteWizard.steps.identity",
  "siteWizard.steps.localization",
  "siteWizard.steps.review",
];

/** Fields that must validate before advancing past each step. */
const STEP_FIELDS: Record<number, (keyof SiteFormValues)[]> = {
  0: ["name", "slug"],
  1: ["language", "expiresAt"],
  2: [],
};

/**
 * Guided, multi-step site creation. Walks the operator through identity and
 * slug, then localization and schedule, then a final review before creating
 * the site. Slug auto-suggests from the name until edited, and is checked
 * for availability in real time.
 */
export function SiteCreateWizard({
  submitting = false,
  onSubmit,
  serverFieldErrors,
}: Props) {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);

  const variantsQuery = useQuery({
    queryKey: ["variants"],
    queryFn: listVariants,
    staleTime: 5 * 60_000,
  });
  const variants = variantsQuery.data?.variants ?? [];

  // Rebuild so zod messages follow the active UI language.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => buildSiteFormSchema(), [i18n.language]);
  const form = useForm<SiteFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      displayName: "",
      slug: "",
      language: "en-US",
      expiresAt: null,
    },
    mode: "onTouched",
  });
  const { register, handleSubmit, setValue, setError, watch, trigger, formState } = form;
  const errors = formState.errors;

  // Auto-suggest slug from name until the user edits the slug field.
  useEffect(() => {
    const sub = watch((values, { name }) => {
      if (name === "name" && !formState.dirtyFields.slug) {
        setValue("slug", slugify(values.name ?? ""), { shouldValidate: false });
      }
    });
    return () => sub.unsubscribe();
  }, [watch, setValue, formState.dirtyFields.slug]);

  // Surface server-side slug failures on the slug field and return to step 1.
  useEffect(() => {
    if (!serverFieldErrors) return;
    for (const [field, message] of Object.entries(serverFieldErrors)) {
      if (message) {
        setError(field as keyof SiteFormValues, { type: "server", message });
        if (field === "slug") setStep(0);
      }
    }
  }, [serverFieldErrors, setError]);

  const name = watch("name");
  const displayName = watch("displayName");
  const slug = watch("slug");
  const language = watch("language");
  const expiresAt = watch("expiresAt");
  const slugPreview = slug || "<slug>";

  // Real-time slug availability (debounced) for step 0. The backend stays
  // authoritative on create; this is just early feedback for the operator.
  const [debouncedSlug, setDebouncedSlug] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSlug(slug), 400);
    return () => clearTimeout(id);
  }, [slug]);
  const slugFormatValid = isValidSlug(slug);
  const slugAvailability = useQuery({
    queryKey: ["checkSlug", debouncedSlug],
    queryFn: () => checkSlug({ slug: debouncedSlug }),
    enabled:
      step === 0 && slugFormatValid && debouncedSlug.length > 0 &&
      debouncedSlug === slug,
    staleTime: 30_000,
    retry: false,
  });

  const goNext = async () => {
    const valid = await trigger(STEP_FIELDS[step]);
    if (!valid) return;
    if (step === 0) {
      if (!isValidSlug(slug)) {
        setError("slug", { type: "manual", message: t("siteWizard.slugInvalid") });
        return;
      }
      if (slugAvailability.data && !slugAvailability.data.available) {
        setError("slug", {
          type: "manual",
          message:
            slugAvailability.data.reason === "taken"
              ? t("siteWizard.slugTaken")
              : t("siteWizard.slugInvalid"),
        });
        return;
      }
    }
    setStep((s) => Math.min(s + 1, STEP_KEYS.length - 1));
  };

  const submit = handleSubmit(async (values) => {
    if (!isValidSlug(values.slug)) {
      setError("slug", { type: "manual", message: t("siteWizard.slugInvalid") });
      setStep(0);
      return;
    }
    await onSubmit(values);
  });

  return (
    <div className="space-y-6">
      <Stepper current={step} />

      {step === 0 && (
        <section className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t("siteWizard.nameLabel")}</Label>
            <Input
              id="name"
              autoComplete="off"
              aria-invalid={errors.name ? true : undefined}
              className={cn(errors.name && "border-destructive focus-visible:ring-destructive")}
              {...register("name")}
            />
            <p className="text-xs text-muted-foreground">{t("siteWizard.nameHint")}</p>
            {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">{t("siteWizard.displayLabel")}</Label>
            <Input
              id="displayName"
              autoComplete="off"
              placeholder={t("siteWizard.displayPlaceholder")}
              aria-invalid={errors.displayName ? true : undefined}
              className={cn(errors.displayName && "border-destructive focus-visible:ring-destructive")}
              {...register("displayName")}
            />
            <p className="text-xs text-muted-foreground">{t("siteWizard.displayHint")}</p>
            {errors.displayName && <p className="text-sm text-destructive">{errors.displayName.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">{t("siteWizard.slugLabel")}</Label>
            <Input
              id="slug"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={errors.slug ? true : undefined}
              className={cn(errors.slug && "border-destructive focus-visible:ring-destructive")}
              {...register("slug")}
            />
            {errors.slug ? (
              <p className="text-sm text-destructive">{errors.slug.message}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                <Trans
                  i18nKey="siteWizard.slugHint"
                  values={{ path: uploaderDisplayPath(slugPreview) }}
                  components={{ c: <code className="font-mono" /> }}
                />
              </p>
            )}
            {!errors.slug && slugFormatValid && debouncedSlug === slug && (
              slugAvailability.isFetching ? (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> {t("siteWizard.checkingAvailability")}
                </p>
              ) : slugAvailability.data ? (
                slugAvailability.data.available ? (
                  <p className="text-xs text-emerald-600 dark:text-emerald-500">
                    {t("siteWizard.slugAvailable")}
                  </p>
                ) : (
                  <p className="text-sm text-destructive">
                    {slugAvailability.data.reason === "taken"
                      ? t("siteWizard.slugTaken")
                      : t("siteWizard.slugInvalidDot")}
                  </p>
                )
              ) : null
            )}
          </div>
        </section>
      )}

      {step === 1 && (
        <section className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="language">{t("siteWizard.languageLabel")}</Label>
            <LanguageCombobox
              id="language"
              value={language}
              options={variants.length > 0 ? variants : language ? [language] : []}
              onChange={(v) => setValue("language", v, { shouldDirty: true })}
              emptyText={
                variants.length === 0 ? t("siteWizard.noVariants") : t("localePicker.empty")
              }
              disabled={variants.length === 0 && !language}
            />
            <p className="text-xs text-muted-foreground">{t("siteWizard.languageHint")}</p>
          </div>

          <div className="space-y-2">
            <Label>{t("siteWizard.expiresLabel")}</Label>
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn("w-full justify-start", !expiresAt && "text-muted-foreground")}
                  >
                    <CalendarIcon />
                    {expiresAt
                      ? format(expiresAt, "PPP", { locale: currentDateFnsLocale() })
                      : t("expiry.none")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={expiresAt ?? undefined}
                    onSelect={(d) => setValue("expiresAt", d ?? null, { shouldDirty: true, shouldValidate: true })}
                    disabled={(d) => d.getTime() <= Date.now()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {expiresAt && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setValue("expiresAt", null, { shouldDirty: true })}
                >
                  {t("common.clear")}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("siteWizard.expiresHint")}
            </p>
            {errors.expiresAt && <p className="text-sm text-destructive">{errors.expiresAt.message}</p>}
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="space-y-3">
          <ReviewRow label={t("siteWizard.reviewName")} value={name} />
          <ReviewRow label={t("siteWizard.reviewDisplay")} value={displayName || "-"} />
          <ReviewRow label={t("siteWizard.reviewUrl")} value={uploaderDisplayPath(slug)} mono />
          <ReviewRow label={t("siteWizard.reviewLanguage")} value={formatLocaleLabelWithTag(language)} />
          <ReviewRow
            label={t("siteWizard.reviewExpires")}
            value={
              expiresAt ? format(expiresAt, "PPP", { locale: currentDateFnsLocale() }) : t("expiry.none")
            }
          />
          <p className="pt-1 text-xs text-muted-foreground">
            {t("siteWizard.reviewNote")}
          </p>
        </section>
      )}

      <div className="flex items-center justify-between border-t pt-4">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setStep((s) => Math.max(s - 1, 0))}
          disabled={step === 0 || submitting}
        >
          <ChevronLeft className="h-4 w-4" /> {t("common.back")}
        </Button>

        {step < STEP_KEYS.length - 1 ? (
          <Button type="button" onClick={goNext}>
            {t("common.continue")} <ChevronRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button type="button" onClick={() => void submit()} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="animate-spin" /> {t("siteWizard.createSite")}
              </>
            ) : (
              <>
                <Check className="h-4 w-4" /> {t("siteWizard.createSite")}
              </>
            )}
          </Button>
        )}
      </div>

    </div>
  );
}

function Stepper({ current }: { current: number }) {
  const { t } = useTranslation();
  return (
    <ol className="flex items-center gap-2 text-xs">
      {STEP_KEYS.map((labelKey, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={labelKey} className="flex items-center gap-2">
            <span
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-medium",
                done && "border-primary bg-primary text-primary-foreground",
                active && "border-primary text-primary",
                !done && !active && "border-muted-foreground/30 text-muted-foreground",
              )}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span className={cn(active ? "font-medium text-foreground" : "text-muted-foreground")}>
              {t(labelKey)}
            </span>
            {i < STEP_KEYS.length - 1 && <span className="mx-1 h-px w-6 bg-border" />}
          </li>
        );
      })}
    </ol>
  );
}

function ReviewRow({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b pb-2 last:border-b-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={cn("text-right text-sm font-medium", mono && "font-mono")}>{value}</span>
    </div>
  );
}
